import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_10: GgxTourDefinition = {
    key: '[RISK-10] Mitigating Risks through Constraining LLMs',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 8,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'The safest reply is one the model was never free to invent',
        subtitle:
            'Guardrails catch problems after the model has spoken. Constraining goes one step ' +
            'earlier: you design the pipeline so the model can only work inside limits you ' +
            'set, on what data it may use, what tone and persona it takes, and what it is ' +
            'allowed to say. Done well, that removes whole categories of risk instead of ' +
            'filtering them one reply at a time. This tour walks through the levels of ' +
            'constraint, how a templated response model is built, a worked example, and then ' +
            'opens the templated version of the credit card chatbot in GGX.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen how the risk profile of a chatbot changes as you move from fully ' +
            'generative to fully constrained, how a templated response model turns free text ' +
            'into template calls filled from verified customer data, and how a guardrail layer ' +
            'checks both the data and the phrasing before anything goes out. You then opened ' +
            'the templated pipeline in GGX and saw that its prompt, phrase library and checker ' +
            'functions are ordinary, versioned resources. Because the output stays structured, ' +
            'it is also the easiest kind of pipeline to test against ground truth. Next, see ' +
            'how people test a pipeline like this before it goes live.',
        learned: [
            'Risk drops as you move from **fully generative to fully constrained**.',
            'A **templated response model** fills templates from verified data.',
            'A **guardrail layer** checks data and phrasing before anything goes out.',
        ],
    },
    chapters: [
        {
            name: 'What constraining means',
            blurb: 'From fully generative to fully constrained',
            icon: 'cap',
            steps: [2, 3],
        },
        {
            name: 'How a constrained model is built',
            blurb: 'Templates, tags, verified data and a guardrail layer',
            icon: 'grid',
            steps: [4, 5],
        },
        {
            name: 'See it on the platform',
            blurb: 'A demo clip, then the templated pipeline itself',
            icon: 'search',
            steps: [6, 7, 8, 9, 10],
        },
    ],
    steps: [
        {
            title: 'Limits set in advance',
            whatHappens: 'next, the levels of constraint and what each one buys you.',
            icon: 'cap',
            description:
                'To constrain a pipeline is to decide up front what the model may do: which ' +
                'customer data it can reach, which tone and persona it speaks in, and which ' +
                'phrases it is allowed to produce. The model still does the understanding, ' +
                'but it fills in a structure you designed rather than composing freely. For a ' +
                'bank that means a chatbot that cannot quote a balance it did not retrieve, ' +
                'cannot promise a fee waiver, and cannot be talked into a new personality, ' +
                'because none of those are on the list. Click Continue.',
            benefits: [
                'Only retrieved data',
                'Only approved phrases',
                'One persona, one tone',
                'Easy to test against truth',
            ],
        },
        {
            title: 'Four levels of constraint',
            whatHappens: 'next, how a templated response model is actually built.',
            icon: 'grid',
            description:
                'The table reads from most constrained on the left to fully generative on the ' +
                'right. Open each tab for what the level buys you and what it costs, then ' +
                'click Continue.',
            tabs: [
                {
                    label: 'Classify only',
                    html:
                        '<p data-ggx-lead>The model only classifies the question and a ' +
                        'predefined answer is returned.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Risk</strong>: no toxicity, no jailbreak, no explicit ' +
                        'bias, no hallucination. The safest level there is.</li>' +
                        '<li><strong>Cost</strong>: only works for a narrow, known set of ' +
                        'questions; anything else gets a hand-off.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="Example reply">"I can help with that. To ' +
                        'confirm, you would like to dispute the {amount} charge at ' +
                        '{merchant}?"</aside>',
                },
                {
                    label: 'Templates and data',
                    html:
                        '<p data-ggx-lead>The model selects approved templates and phrases and ' +
                        'code fills in the customer data.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Risk</strong>: very low. Toxicity only if the templated ' +
                        'data itself can contain unconstrained text; implicit bias only if ' +
                        'the data drives different treatment.</li>' +
                        '<li><strong>Cost</strong>: a template library to design and ' +
                        'maintain, and multi-part questions need enough templates to cover ' +
                        'them.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">This is the level the templated ' +
                        'pipelines in this project use, and where the rest of this tour ' +
                        'goes.</aside>',
                },
                {
                    label: 'Generate and contain',
                    html:
                        '<p data-ggx-lead>Free text, checked against a large permitted list of ' +
                        'words and phrases before it goes out.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Risk</strong>: hallucination, jailbreak and business ' +
                        'policy adherence come back as concerns; the permitted list catches ' +
                        'the obvious slips.</li>' +
                        '<li><strong>Cost</strong>: a large phrase database, often built by ' +
                        'starting from the templated level.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">A middle ground for teams that need ' +
                        'more natural replies but cannot accept open generation.</aside>',
                },
                {
                    label: 'Output guardrails',
                    html:
                        '<p data-ggx-lead>Full generation from a prompt with detailed ' +
                        'instructions, plus guardrails on the output.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Risk</strong>: every category is live (toxicity, ' +
                        'hallucination, jailbreak, bias, policy) and depends on the guardrails ' +
                        'holding.</li>' +
                        '<li><strong>Cost</strong>: the guardrail stack itself, and the ' +
                        'evaluations that prove it works.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">The most natural conversation, and ' +
                        'the one that needs the most measurement. Mitigating Risks through ' +
                        'Guardrails covers that stack.</aside>',
                },
            ],
            image: {
                src: 'https://genguardx.ai/tours/risk-10-constraint-levels.png',
                title: 'Constraints offer an effective way to eliminate or materially reduce risks',
                caption:
                    'Risk categories by level of constraint, from classify-only to fully generative.',
            },
        },
        {
            title: 'How a templated response model works',
            whatHappens: 'next, a worked example with a real customer question.',
            icon: 'grid',
            description:
                'Follow the flow on the left of the diagram:' +
                '<ul data-ggx-list>' +
                "<li><strong>Inputs</strong>: the conversation so far, the customer's " +
                'transaction data, and a prompt that carries the approved templates and ' +
                'phrases.</li>' +
                '<li><strong>Raw output</strong>: instead of writing text, the model returns ' +
                'a sequence of template calls, some with tags that point at customer ' +
                'data.</li>' +
                '<li><strong>Fill</strong>: plain code replaces the tags with real values from ' +
                'the record.</li>' +
                '<li><strong>Guardrail</strong>: before the reply goes out, two checks: every ' +
                'piece of data came from the customer record, and every phrase matches the ' +
                'approved list.</li>' +
                '</ul>' +
                '<aside data-ggx-use="Design work">The considerations on the right: scope the ' +
                'domain, decide how rigid or modular the templates are, plan how data fields ' +
                'and multiple cards are handled, then test coverage and naturalness and tune ' +
                'the templates so they do not over-block.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-10-templated-model.png',
                title: 'Implementation of constrained response models for a customer service chatbot',
                caption: 'Process flow on the left, design considerations on the right.',
            },
        },
        {
            title: 'A worked example',
            whatHappens: 'next, a short demo of the constrained pipeline on the platform.',
            icon: 'list',
            description:
                'A customer asks <strong>"What is the balance on my Mastercard?"</strong>. The ' +
                'model does not write a sentence. It picks from the template library and ' +
                'returns template calls with tags:' +
                '<pre data-ggx-code="What the model returns" data-ggx-wrap data-ggx-readonly>["Your", "{cards.1.card_type} card ' +
                'ending in {cards.1.last4}", "has", "a", "balance of ' +
                '{cards.1.current_balance}"]\n' +
                '\n' +
                '# filled from the retrieved card record, with formatting rules (MC -> ' +
                'Mastercard, add currency)\n' +
                '"Your Mastercard ending 3213 has a balance of 1000 dollars"</pre>' +
                '<aside data-ggx-use="Why it matters">The reply is built from approved phrases ' +
                'and verified data paths, so it is both natural and safe. And because the ' +
                'structured fields sit right beside it, accuracy testing is a direct ' +
                'comparison against ground truth.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-10-worked-example.png',
                title: 'Overview of the example implementation used in the sandbox',
                caption:
                    'Templates, tagged output, filled values, plain-English reply and the structured field for testing.',
            },
        },
        {
            title: 'The constrained pipeline in action',
            whatHappens: 'next, we open the templated pipeline ourselves.',
            icon: 'compass',
            description:
                'This clip from the Responsible AI webinar demos the constrained pipeline on ' +
                'the platform. Watch it, then click Continue and we will open the templated ' +
                'response pipeline and look at the prompt, the phrase library and the checker ' +
                'functions behind it.',
            video: {
                src: 'https://genguardx.ai/tours/risk-webinar-risk-mitigation.mp4',
                title: 'Demo of a constrained pipeline on the platform',
            },
        },
        {
            target: '[test-id="search-button"]',
            title: 'Find the templated pipeline',
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'The credit card chatbot exists in this project in a templated version, built ' +
                'exactly the way the diagram described. The quickest way there is the global ' +
                'search, which also opens with <strong>Ctrl+K</strong>. Click ' +
                '<strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the templated pipeline',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the templated pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Customer Response Pipeline (for Credit Cards) - ' +
                'templated</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Customer Response Pipeline (for Credit Cards) - templated v1',
            scrollIntoView: true,
            title: 'Open the templated pipeline',
            whatHappens: "the pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'This is the response half of the chatbot, rebuilt to answer only from ' +
                'approved templates. Click <strong>Customer Response Pipeline (for Credit ' +
                'Cards) - templated</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Customer Response Pipeline (for Credit Cards) - templated',
        },
        {
            target: '[test-id="resource-prompts"]',
            climbTo: '.card',
            frame: true,
            scrollIntoView: true,
            dockCard: 'below',
            title: 'Everything from the diagram, as resources',
            whatHappens: 'the tour wraps up.',
            icon: 'pulse',
            description:
                'The <strong>Resources Used</strong> card is the diagram made concrete:' +
                '<ul data-ggx-list>' +
                '<li><strong>Prompt</strong>: <code>existing_customer_credit_card_related_' +
                'response_prompt_templated</code> carries the approved templates and ' +
                'phrases.</li>' +
                '<li><strong>RAG</strong>: <code>retrieve_credit_card_details_templated_' +
                'phrases</code> brings back both the card data and the matching phrase ' +
                'library.</li>' +
                '<li><strong>Guardrail layer</strong>: <code>verify_templated_response</code> ' +
                'and <code>check_template_data_rules</code> confirm that every phrase is ' +
                'approved and every value came from the customer record.</li>' +
                '<li><strong>Renderer</strong>: <code>render_response_from_templates</code> ' +
                'turns the filled templates into the plain-English reply.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">Each one is versioned and reviewable on its ' +
                'own. Open any of them to read exactly what the model is and is not allowed to ' +
                'say.</aside>' +
                'Click Continue to finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
